import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Save, ChevronLeft, Image as ImageIcon, Video, Trash2, Plus, Sparkles, Wand2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

export default function Editor() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  // Form State
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [type, setType] = useState<'blog' | 'case_study'>('blog');
  const [category, setCategory] = useState('');
  const [author, setAuthor] = useState('GreenTick Team');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [mediaUrls, setMediaUrls] = useState<string[]>([]);
  const [newMediaUrl, setNewMediaUrl] = useState('');

  useEffect(() => {
    // Basic auth check
    const session = localStorage.getItem('gt_admin_session');
    if (!session) {
      navigate('/admin/login');
      return;
    }

    if (id) {
      fetchPost(id);
    }
  }, [id, navigate]);

  async function fetchPost(postId: string) {
    const { data, error } = await supabase
      .from('cms_posts')
      .select('*')
      .eq('id', postId)
      .single();

    if (error) {
      console.error('Error fetching post:', error);
      alert('Could not find post');
      navigate('/admin/dashboard');
    } else if (data) {
      setTitle(data.title);
      setSlug(data.slug);
      setExcerpt(data.excerpt || '');
      setContent(data.content);
      setType(data.type);
      setCategory(data.category || '');
      setAuthor(data.author || '');
      setThumbnailUrl(data.thumbnail_url || '');
      setMediaUrls(data.media_urls || []);
    }
  }

  const generateSlug = () => {
    const s = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    setSlug(s);
  };

  const addMedia = () => {
    if (newMediaUrl && !mediaUrls.includes(newMediaUrl)) {
      setMediaUrls([...mediaUrls, newMediaUrl]);
      setNewMediaUrl('');
    }
  };

  const removeMedia = (index: number) => {
    setMediaUrls(mediaUrls.filter((_, i) => i !== index));
  };

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);

    const postData = {
      title,
      slug,
      excerpt,
      content,
      type,
      category,
      author,
      thumbnail_url: thumbnailUrl,
      media_urls: mediaUrls,
      published: true
    };

    let error;
    if (id) {
       ({ error } = await supabase
        .from('cms_posts')
        .update(postData)
        .eq('id', id));
    } else {
       ({ error } = await supabase
        .from('cms_posts')
        .insert([postData]));
    }

    if (error) {
      alert('Error saving: ' + error.message);
    } else {
      navigate('/admin/dashboard');
    }
    setLoading(false);
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <main className="pt-32 pb-24 home-section-bg">
        <div className="absolute inset-0 bg-grid-pattern opacity-60 pointer-events-none"></div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors uppercase font-bold tracking-widest text-xs"
              >
                <ChevronLeft className="w-4 h-4" />
                Back to Dashboard
              </button>
              <div className="flex items-center gap-4">
                 <div className="text-xs text-slate-400 uppercase tracking-widest font-bold">
                    {id ? 'Editing Mode' : 'Drafting Masterpiece'}
                 </div>
              </div>
            </div>

            <form onSubmit={handleSave} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Contents */}
              <div className="lg:col-span-2 space-y-6">
                <div className="glass p-8 rounded-3xl border border-white/10 shadow-2xl space-y-6">
                  <div>
                    <label htmlFor="master-title" className="block text-sm font-bold text-slate-400 uppercase tracking-widest mb-3">Master Title</label>
                    <input
                      id="master-title"
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      onBlur={() => !slug && generateSlug()}
                      className="w-full bg-slate-900 border border-white/10 rounded-xl px-6 py-4 text-2xl font-bold text-white focus:outline-none focus:border-teal-500 transition-all placeholder:text-slate-800"
                      placeholder="The Future of AI Recruitment..."
                      required
                    />
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label htmlFor="url-slug" className="block text-sm font-bold text-slate-400 uppercase tracking-widest mb-3">URL Slug</label>
                      <div className="relative">
                         <input
                          id="url-slug"
                          type="text"
                          value={slug}
                          onChange={(e) => setSlug(e.target.value)}
                          className="w-full bg-slate-900 border border-white/10 rounded-xl pl-6 pr-12 py-3 text-slate-300 focus:outline-none focus:border-blue-500 font-mono text-sm"
                          placeholder="future-of-ai"
                          required
                        />
                        <button 
                          type="button" 
                          onClick={generateSlug}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-teal-400 p-1"
                          title="Auto-generate from title"
                        >
                           <Wand2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="short-excerpt" className="block text-sm font-bold text-slate-400 uppercase tracking-widest mb-3">Short Excerpt</label>
                    <textarea
                      id="short-excerpt"
                      value={excerpt}
                      onChange={(e) => setExcerpt(e.target.value)}
                      className="w-full bg-slate-900 border border-white/10 rounded-xl px-6 py-3 text-slate-300 focus:outline-none focus:border-teal-500 transition-all h-24"
                      placeholder="Briefly describe the content..."
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-3">
                       <label htmlFor="article-body" className="text-sm font-bold text-slate-400 uppercase tracking-widest">Article Body (Markdown Supported)</label>
                       <div className="text-[10px] text-teal-500 font-bold uppercase border border-teal-500/30 px-2 py-0.5 rounded-md flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                           Rich Content
                       </div>
                    </div>
                    <textarea
                      id="article-body"
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      className="w-full bg-slate-900 border border-white/10 rounded-xl px-6 py-6 text-slate-300 focus:outline-none focus:border-teal-500 transition-all min-h-[400px] font-mono leading-relaxed"
                      placeholder="Start writing here..."
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Sidebar Settings */}
              <div className="space-y-6">
                <div className="glass p-6 rounded-3xl border border-white/10 shadow-2xl space-y-6">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-6 py-4 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl font-bold hover:shadow-2xl hover:shadow-teal-500/50 transition-all flex items-center justify-center gap-2 group"
                  >
                    <Save className="w-5 h-5 group-hover:scale-110 transition-transform" />
                    {loading ? 'Processing...' : 'Deploy Content'}
                  </button>

                  <div className="pt-6 border-t border-white/5 space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Content Type</label>
                      <div className="grid grid-cols-2 gap-2">
                         <button
                           type="button"
                           onClick={() => setType('blog')}
                           className={`px-4 py-2 rounded-lg text-xs font-bold border transition-all ${
                             type === 'blog' ? 'bg-teal-500/20 border-teal-500 text-teal-400 shadow-lg shadow-teal-500/10' : 'bg-white/5 border-white/10 text-slate-400'
                           }`}
                         >
                           Blog Post
                         </button>
                         <button
                           type="button"
                           onClick={() => setType('case_study')}
                           className={`px-4 py-2 rounded-lg text-xs font-bold border transition-all ${
                             type === 'case_study' ? 'bg-purple-500/20 border-purple-500 text-purple-400 shadow-lg shadow-purple-500/10' : 'bg-white/5 border-white/10 text-slate-400'
                           }`}
                         >
                           Case Study
                         </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Category Tag</label>
                      <input
                        type="text"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="w-full bg-slate-900 border border-white/10 rounded-lg px-4 py-2 text-sm text-slate-300 focus:outline-none focus:border-teal-500"
                        placeholder="Technology, Hiring, Enterprise"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Featured Image URL</label>
                      <input
                        type="text"
                        value={thumbnailUrl}
                        onChange={(e) => setThumbnailUrl(e.target.value)}
                        className="w-full bg-slate-900 border border-white/10 rounded-lg px-4 py-2 text-sm text-slate-300 focus:outline-none focus:border-teal-500 mb-2"
                        placeholder="https://images.unsplash..."
                      />
                      {thumbnailUrl && (
                        <div className="relative aspect-video rounded-lg overflow-hidden border border-white/10">
                           <img src={thumbnailUrl} alt="Preview" className="object-cover w-full h-full" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Media Gallery Section */}
                <div className="glass p-6 rounded-3xl border border-white/10 shadow-2xl space-y-6">
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Media Stack (Photos/Videos)</label>
                    <div className="flex gap-2 mb-4">
                      <input
                        type="text"
                        value={newMediaUrl}
                        onChange={(e) => setNewMediaUrl(e.target.value)}
                        className="flex-1 bg-slate-900 border border-white/10 rounded-lg px-4 py-2 text-xs text-slate-300 focus:outline-none focus:border-teal-500"
                        placeholder="Paste URL here..."
                      />
                      <button
                        type="button"
                        onClick={addMedia}
                        className="p-2 bg-teal-500/10 border border-teal-500/20 text-teal-400 rounded-lg hover:bg-teal-500/20 transition-all"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="space-y-3">
                      {mediaUrls.map((url, index) => (
                        <div key={index} className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-xl group overflow-hidden">
                          <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
                             {url.includes('video') || url.includes('youtube') ? <Video className="w-4 h-4 text-purple-400" /> : <ImageIcon className="w-4 h-4 text-teal-400" />}
                          </div>
                          <span className="text-[10px] text-slate-400 truncate flex-1 font-mono">{url}</span>
                          <button
                            type="button"
                            onClick={() => removeMedia(index)}
                            className="p-1.5 text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
